import { ConversionComparison } from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { PipelineRatio } from '@partner/feature-modules/dashboard/domain/model/pipeline-health.model';
import {
  ApprovalRateInsight,
  ApprovalRateState,
  SmartInsight,
  SmartInsights,
  SmartInsightTone,
  SmartInsightType,
} from '@partner/feature-modules/dashboard/domain/model/smart-insights.model';
import { WebPaths } from '@partner/shared/SharedConstants';
import { adaptDashboardLink, DashboardLinkResponse } from './dashboard-link-api.model';
import { PipelineRatioResponse } from './pipeline-health-api.model';
import { TrendComparisonResponse } from './trend-metrics-api.model';
import { DashboardPageSlug } from '../../dashboard.data';

/** 200 body of `GET api/dashboard/smart-insights` (contract v1.4); instants are ISO-8601 UTC strings. */
export interface SmartInsightsResponse {
  asOfUtc: string;
  period: { from: IsoDate; to: IsoDate };
  insights: SmartInsightResponse[];
  /** Envelope symmetry with the sibling responses; always null on 200. */
  validationMessage: string | null;
}

/** `type` is a plain string: a value this client does not know must still parse, and is dropped. */
export interface SmartInsightResponse {
  type: string;
  tone: SmartInsightTone;
  documentsWaiting: DocumentsWaitingInsightResponse | null;
  approvalRate: ApprovalRateInsightResponse | null;
  bestMonth: BestMonthInsightResponse | null;
  teamSeats: TeamSeatsInsightResponse | null;
  link: DashboardLinkResponse;
}

export interface DocumentsWaitingInsightResponse {
  applications: number;
  thresholdDays: number;
}

/** `state` is a plain string like `type`; the four figures are null under `insufficient`. */
export interface ApprovalRateInsightResponse {
  state: string;
  current: PipelineRatioResponse | null;
  prior: PipelineRatioResponse | null;
  deltaPoints: number | null;
  team: PipelineRatioResponse | null;
  comparison: TrendComparisonResponse;
  minSubmitted: number;
}

export interface BestMonthInsightResponse {
  monthToDateAmount: number;
  bestMonth: string;
  bestAmount: number;
}

export interface TeamSeatsInsightResponse {
  seats: number;
  daysIdle: number;
}

/** The contract's closed route set; an entry pointing anywhere else is dropped. */
export const SMART_INSIGHT_ROUTES: readonly string[] = [
  WebPaths.Submissions,
  WebPaths.DashboardPage(DashboardPageSlug.CONVERSION_METRICS),
  WebPaths.Team,
];

const adaptRatio = (ratio: PipelineRatioResponse): PipelineRatio => ({
  percent: ratio.percent ?? null,
  numerator: ratio.numerator,
  denominator: ratio.denominator,
});

const adaptComparison = (comparison: TrendComparisonResponse): ConversionComparison => ({
  period: comparison.period,
  from: comparison.from,
  to: comparison.to,
  cutoffUtc: new Date(comparison.cutoffUtc),
});

/** Null for an unknown state, an insufficient state without its floor, or a compared state missing a figure. */
function adaptApprovalRate(approval: ApprovalRateInsightResponse): ApprovalRateInsight | null {
  if (approval.state === ApprovalRateState.INSUFFICIENT) {
    if (typeof approval.minSubmitted !== 'number') {
      return null;
    }
    return {
      state: ApprovalRateState.INSUFFICIENT,
      minSubmitted: approval.minSubmitted,
      comparison: adaptComparison(approval.comparison),
    };
  }
  if (approval.state !== ApprovalRateState.MOVED && approval.state !== ApprovalRateState.STEADY) {
    return null;
  }
  const { current, prior, deltaPoints } = approval;
  if (!current || !prior || deltaPoints === null || deltaPoints === undefined) {
    return null;
  }
  return {
    state: approval.state,
    current: adaptRatio(current),
    prior: adaptRatio(prior),
    deltaPoints,
    team: approval.team ? adaptRatio(approval.team) : null,
    comparison: adaptComparison(approval.comparison),
  };
}

/** Null for an unknown type, a missing payload or a route outside the closed set. */
function adaptInsight(insight: SmartInsightResponse): SmartInsight | null {
  if (!insight.link || !SMART_INSIGHT_ROUTES.includes(insight.link.route)) {
    return null;
  }
  const tone = insight.tone;
  const link = adaptDashboardLink(insight.link);
  switch (insight.type) {
    case SmartInsightType.DOCUMENTS_WAITING:
      return insight.documentsWaiting
        ? {
            type: SmartInsightType.DOCUMENTS_WAITING,
            tone,
            link,
            documentsWaiting: {
              applications: insight.documentsWaiting.applications,
              thresholdDays: insight.documentsWaiting.thresholdDays,
            },
          }
        : null;
    case SmartInsightType.APPROVAL_RATE: {
      const approvalRate = insight.approvalRate ? adaptApprovalRate(insight.approvalRate) : null;
      return approvalRate ? { type: SmartInsightType.APPROVAL_RATE, tone, link, approvalRate } : null;
    }
    case SmartInsightType.BEST_MONTH:
      return insight.bestMonth
        ? {
            type: SmartInsightType.BEST_MONTH,
            tone,
            link,
            bestMonth: {
              monthToDateAmount: insight.bestMonth.monthToDateAmount,
              bestMonth: insight.bestMonth.bestMonth,
              bestAmount: insight.bestMonth.bestAmount,
            },
          }
        : null;
    case SmartInsightType.TEAM_SEATS:
      return insight.teamSeats
        ? {
            type: SmartInsightType.TEAM_SEATS,
            tone,
            link,
            teamSeats: { seats: insight.teamSeats.seats, daysIdle: insight.teamSeats.daysIdle },
          }
        : null;
    default:
      return null;
  }
}

export const adaptSmartInsights = (response: SmartInsightsResponse): SmartInsights => ({
  asOfUtc: new Date(response.asOfUtc),
  period: { from: response.period.from, to: response.period.to },
  insights: (response.insights ?? [])
    .map(adaptInsight)
    .filter((insight: SmartInsight | null): insight is SmartInsight => insight !== null),
});
